import styled from "@emotion/styled";
import { Button, IconButton, Typography } from "@kaltura/ds-react-components";
import { Close16Icon } from "@kaltura/ds-react-icons";
import { translate, useMediaQuery } from "@kaltura/mediaspace-shared-utils";
import { usePwaStore } from "../usePwaStore";
import { isDesktop } from "react-device-detect";
import { useIsInsidePwa } from "../useIsInsidePwa";
import { useState } from "react";
import { QrCodeModal } from "../qr-code-modal/QrCodeModal";
import { SiteLogo } from "../site-logo/SiteLogo";
import {
    ManualInstallationInstructionsDrawer,
    supportsManualInstallation,
} from "../instructions/ManualInstallationInstructionsDrawer";

export interface InstallBannerProps {
    /** The app name shown in the installation prompt text. */
    appName: string;
    /** URL of the 48×48 app icon. */
    appIconUrl: string;
    /** URL of the header background image */
    backgroundImageUrl?: string;
}

/**
 * Sticky top banner prompting the user to install the PWA.
 */
export const InstallBanner = ({ appName, appIconUrl, backgroundImageUrl }: InstallBannerProps) => {
    const isInsidePwa = useIsInsidePwa();
    const { canInstall, install, dismiss, dismissed } = usePwaStore();

    const [showQrCode, setShowQrCode] = useState(false);
    const [showManualInstallationInstructions, setShowManualInstallationInstructions] = useState(false);

    const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));

    if (isInsidePwa || (!supportsManualInstallation && !canInstall) || dismissed) {
        return null;
    }

    const handleInstall = () => {
        if (supportsManualInstallation) {
            setShowManualInstallationInstructions(true);
        }
        else {
            install();
        }
    };

    return (
        <>
            <StyledBanner role={"banner"} backgroundImageUrl={backgroundImageUrl}>
                <SiteLogo src={appIconUrl} />

                <StyledContent>
                    <StyledText variant={isSmallScreen ? "body2" : "body1Highlight"} component={"div"}>
                        {translate("Install %1 on your phone, tablet, or computer.", [appName])}
                    </StyledText>

                    {isDesktop && (
                        <StyledInstallButton variant={"pill"} color={"translucent"} onClick={() => setShowQrCode(true)}>
                            {translate("Install via QR code")}
                        </StyledInstallButton>
                    )}

                    <StyledWhiteInstallButton variant={"pill"} color={"translucent"} onClick={handleInstall}>
                        {translate("Install app")}
                    </StyledWhiteInstallButton>
                </StyledContent>

                <IconButton
                    variant={"borderless"}
                    color={"translucent"}
                    onClick={dismiss}
                    aria-label={translate("Dismiss install banner")}
                >
                    <Close16Icon />
                </IconButton>
            </StyledBanner>

            <QrCodeModal open={showQrCode} onClose={() => setShowQrCode(false)} />

            <ManualInstallationInstructionsDrawer
                open={showManualInstallationInstructions}
                onClose={() => setShowManualInstallationInstructions(false)}
                appName={appName}
                appIconUrl={appIconUrl}
                isDesktop={isDesktop}
            />
        </>
    );
};

const StyledBanner = styled(
    "div",
    { shouldForwardProp: (prop) => prop !== "backgroundImageUrl" }
)<{ backgroundImageUrl?: string }>(({ theme, backgroundImageUrl }) => ({
    width: "100%",
    backgroundColor: theme.kaltura.palette.primary.main,
    position: "relative",
    ...(
        backgroundImageUrl && {
            backgroundImage: `url(${backgroundImageUrl})`,
            backgroundSize: "100% 100%",
            backgroundRepeat: "no-repeat",
        }
    ),
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: theme.spacing(2),
    gap: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
        gap: theme.spacing(1),
        paddingRight: theme.spacing(1),
    },
}));

const StyledContent = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    gap: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
        gap: theme.spacing(1),
        justifyContent: "space-between",
    },
}));

const StyledText = styled(Typography)(({ theme }) => ({
    color: theme.kaltura.palette.primary.contrastText,
    margin: 0,
}));

const StyledInstallButton = styled(Button)({
    flexShrink: 0,
    borderRadius: 20,
});

const StyledWhiteInstallButton = styled(StyledInstallButton)({
    // The button is translucent and intentionally doesn't use the theme
    color: "#333",
    backgroundColor: "#fff",
    ":hover": {
        backgroundColor: "#fffd",
    },
});
